import { FC, PropsWithChildren, useRef, useState } from 'react';
import omit from 'lodash/omit';
import { isBrowser } from '@belong/env';
import { useLockBodyScroll } from '@belong/react-hooks';
import { ID, IGlobalHeaderUrls, IHeaderMenu, IPageAlert, USER_TYPE } from '@belong/types';
import { Alert, BackToTopTrigger, RichText, SkipLink } from '@belong/ui-components';
import { urls } from '@belong/url-utils';
import { GlobalHeaderDesktop } from '../GlobalHeaderDesktop';
import { GlobalHeaderMobile } from '../GlobalHeaderMobile';
import { ContentStretcher, NavHeader } from './styles';

export interface IGlobalHeader {
  headerMenuContent: IHeaderMenu;
  currentUrl?: string;
  isLoggedIn: boolean;
  userType?: USER_TYPE;
  pageAlert?: IPageAlert;
  showBackToTopTrigger: boolean;
  // hide the 'My services' and 'My profile' links from the header.
  hideMyAccountLinks?: boolean;
  isNbnOrderCartReady?: boolean;
}

const HEADER_URLS: IGlobalHeaderUrls = {
  home: urls.HOME,
  blog: urls.BLOG,
  coverageMap: urls.COVERAGE_MAP,
  dataPlans: urls.MOBILE_DATA_PLANS,
  login: urls.LOGIN,
  mobilePlans: urls.MOBILE_PLANS,
  myProfile: urls.MY_PROFILE,
  myServices: urls.MY_SERVICES,
  nbnPlans: urls.NBN_PLANS,
  internationalRoaming: urls.INTERNATIONAL_ROAMING,
  ourMobileNetwork: urls.OUR_MOBILE_NETWORK,
  secondLifeDevices: urls.SECOND_LIFE_DEVICES,
  support: urls.SUPPORT,
  search: urls.SEARCH
};

export const GlobalHeader: FC<PropsWithChildren<IGlobalHeader>> = ({
  headerMenuContent,
  currentUrl,
  isLoggedIn,
  userType,
  pageAlert,
  showBackToTopTrigger,
  hideMyAccountLinks,
  isNbnOrderCartReady,
  children
}) => {
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const [isDesktopExpanded, setIsDesktopExpanded] = useState(false);
  const [isAlertDismissed, setIsAlertDismissed] = useState(pageAlert?.dismissed);
  const navHeaderRef = useRef<HTMLDivElement>(null);

  // BEGIN :HAS() HACK

  const supportsHas = isBrowser() && CSS?.supports('selector(:has(.foo))');
  useLockBodyScroll(!supportsHas && isMobileExpanded);

  // END :HAS() HACK

  return (
    <>
      {showBackToTopTrigger && !isMobileExpanded && <BackToTopTrigger />}

      <NavHeader ref={navHeaderRef}>
        {pageAlert && !isAlertDismissed && (
          <aside aria-label="information">
            <Alert
              {...omit(pageAlert, 'content')}
              onDismiss={() => {
                setIsAlertDismissed(true);
              }}
            >
              {pageAlert.content ? <RichText html={pageAlert.content} /> : pageAlert.children}
            </Alert>
          </aside>
        )}
        <header role="banner">
          <SkipLink href={`#${ID.CONTENT}`}>{headerMenuContent.a11ySkipToContent || 'Skip to content'}</SkipLink>

          <GlobalHeaderMobile
            headerMenuContent={headerMenuContent}
            currentUrl={currentUrl}
            isLoggedIn={isLoggedIn}
            hideMyAccountLinks={hideMyAccountLinks}
            urls={HEADER_URLS}
            onExpanded={x => setIsMobileExpanded(x)}
            topHeaderRef={navHeaderRef}
            isNbnOrderCartReady={isNbnOrderCartReady}
          />

          <GlobalHeaderDesktop
            headerMenuContent={headerMenuContent}
            currentUrl={currentUrl}
            isLoggedIn={isLoggedIn}
            userType={userType}
            hideMyAccountLinks={hideMyAccountLinks}
            urls={HEADER_URLS}
            onExpanded={x => setIsDesktopExpanded(x)}
            navHeaderRef={navHeaderRef}
            isNbnOrderCartReady={isNbnOrderCartReady}
          />
        </header>
      </NavHeader>

      <ContentStretcher
        aria-hidden={isMobileExpanded || isDesktopExpanded}
        tabIndex={isMobileExpanded || isDesktopExpanded ? -1 : undefined}
      >
        {children}
      </ContentStretcher>
    </>
  );
};

GlobalHeader.displayName = 'GlobalHeader';
